import { AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSettingsFilters } from '@/contexts/SettingsFilterContext';
import { useState, useEffect } from 'react';
import { AVAILABLE_INDUSTRIES } from '@/contexts/SettingsFilterContext';

interface FilterIndicatorBannerProps {
  /** Optional callback when banner is dismissed */
  onDismiss?: () => void;
}

/**
 * FilterIndicatorBanner - Shows when Settings filters are active
 *
 * Alerts users when industry/company/group filters are hiding results.
 * Prevents confusion when newly created items don't appear in the list.
 */
export function FilterIndicatorBanner({ onDismiss }: FilterIndicatorBannerProps) {
  const {
    selectedIndustries,
    selectedCompanyIds,
    selectedGroupIds,
    companies,
    groups,
    clearAllFilters,
    hasActiveFilters,
  } = useSettingsFilters();

  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when filters change
  useEffect(() => {
    setDismissed(false);
  }, [selectedIndustries, selectedCompanyIds, selectedGroupIds]);

  // Don't show if no filters are active or if dismissed
  if (!hasActiveFilters || dismissed) {
    return null;
  }

  // Build filter description
  const getFilterDescription = () => {
    const parts: string[] = [];

    // Industry filter
    if (selectedIndustries.length < AVAILABLE_INDUSTRIES.length) {
      const industryNames = selectedIndustries.map(i => {
        switch (i) {
          case 'film': return 'Film';
          case 'event': return 'Event';
          case 'gov': return 'Government';
          case 'utility': return 'Utility';
          case 'relief': return 'Relief';
          default: return i;
        }
      });
      if (industryNames.length === 1) {
        parts.push(`${industryNames[0]} industry only`);
      } else {
        parts.push(`${industryNames.join(', ')} industries`);
      }
    }

    // Company filter
    if (selectedCompanyIds.length < companies.length && selectedCompanyIds.length > 0) {
      if (selectedCompanyIds.length === 1) {
        const company = companies.find(c => c.id === selectedCompanyIds[0]);
        parts.push(`${company?.name || 'one company'}`);
      } else {
        parts.push(`${selectedCompanyIds.length} companies`);
      }
    }

    // Group filter
    if (selectedGroupIds.length < groups.length && selectedGroupIds.length > 0) {
      if (selectedGroupIds.length === 1) {
        const group = groups.find(g => g.id === selectedGroupIds[0]);
        parts.push(`${group?.name || 'one group'}`);
      } else {
        parts.push(`${selectedGroupIds.length} groups`);
      }
    }

    return parts.length > 0 ? parts.join(', ') : 'filters active';
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const handleClearFilters = () => {
    clearAllFilters();
    setDismissed(true);
  };

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2.5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0">
          <span className="text-sm font-medium text-amber-300 whitespace-nowrap">Filters active:</span>
          <span className="text-sm text-amber-400/90 truncate">
            Showing {getFilterDescription()}. Some items may be hidden.
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="h-7 px-3 text-amber-300 hover:text-amber-200 hover:bg-amber-500/20 whitespace-nowrap"
        >
          Show All
        </Button>
        <button
          onClick={handleDismiss}
          className="h-7 w-7 flex items-center justify-center rounded-md text-amber-400 hover:text-amber-300 hover:bg-amber-500/20 transition-colors"
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
